import { timelineExpandedAtom } from '../../atoms/workspace';
import { trpc } from '../../lib/trpc';
import { shouldQueryWorkspaceActivity } from './activityQuery';
import { ActionIcon } from '@mantine/core';
import dayjs from 'dayjs';
import { useSetAtom } from 'jotai';
import { ChevronLeft } from 'lucide-react';
import { useMemo } from 'react';

const getDotSize = (type: string): number => {
  if (type === 'COMMENT_ADDED' || type === 'COMMENT_EDITED') {
    return 10;
  }

  if (
    type === 'TODO_COMPLETED' ||
    type === 'TODO_ADDED' ||
    type === 'FILE_ADDED'
  ) {
    return 7;
  }

  return 5;
};

type TimelineStripProps = {
  readonly workspaceId: null | string;
};

export const TimelineStrip = ({ workspaceId }: TimelineStripProps) => {
  const setTimelineExpanded = useSetAtom(timelineExpandedAtom);

  const { now, twentyFourHoursAgo } = useMemo(
    () => ({
      now: new Date(),
      twentyFourHoursAgo: dayjs().subtract(24, 'hours').toDate(),
    }),
    [],
  );

  const { data: result } = trpc.activity.list.useQuery(
    {
      from: twentyFourHoursAgo,
      to: now,
      workspaceId: workspaceId as string,
    },
    { enabled: shouldQueryWorkspaceActivity(workspaceId) },
  );

  const entries = result?.ok ? result.entries : [];

  // Calculate vertical position (0 = top = most recent, 100 = bottom = 24h ago)
  const startMs = twentyFourHoursAgo.getTime();
  const nowMs = now.getTime();
  const totalMs = nowMs - startMs;

  const getTopPercent = (createdAt: Date) => {
    const elapsed = createdAt.getTime() - startMs;
    // Most recent at top: invert so recent = low percent
    return 100 - (elapsed / totalMs) * 100;
  };

  return (
    <div className="w-[48px] flex-shrink-0 flex flex-col items-center relative bg-white/50 dark:bg-slate-800/50 border-l border-black/10 dark:border-white/10">
      {/* TODAY label */}
      <div className="py-2 text-[9px] font-semibold tracking-widest text-black/40 dark:text-white/40 rotate-0">
        TODAY
      </div>

      {/* Timeline bar area */}
      <div className="flex-1 w-full relative px-[18px]">
        {/* Vertical line */}
        <div className="absolute left-1/2 top-0 bottom-0 w-[1px] -translate-x-1/2 bg-black/15 dark:bg-white/15" />

        {/* Time labels */}
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-full text-[8px] text-black/30 dark:text-white/30 whitespace-nowrap -ml-3">
          now
        </div>
        <div className="absolute left-1/2 bottom-0 -translate-x-1/2 translate-y-full text-[8px] text-black/30 dark:text-white/30 whitespace-nowrap -ml-3">
          24h
        </div>

        {/* Activity dots */}
        {entries.map((entry) => {
          const size = getDotSize(entry.type);
          const topPercent = getTopPercent(new Date(entry.createdAt));
          return (
            <div
              className="absolute left-1/2 -translate-x-1/2 rounded-full bg-sky-500 dark:bg-sky-400 opacity-70"
              key={entry.id}
              style={{
                height: size,
                marginTop: -(size / 2),
                top: `${topPercent}%`,
                width: size,
              }}
              title={`${entry.processTitle} — ${entry.type} at ${dayjs(entry.createdAt).format('HH:mm')}`}
            />
          );
        })}
      </div>

      {/* Expand button */}
      <ActionIcon
        className="mb-2 mt-2"
        color="gray"
        onClick={() => setTimelineExpanded(true)}
        title="Expand timeline"
        variant="transparent"
      >
        <ChevronLeft size={14} />
      </ActionIcon>
    </div>
  );
};
